import gql from 'graphql-tag';

/**
 * Finance App API queries/mutations.
 * These call the hub3api-finance GraphQL resolvers which proxy
 * to the Shootsta Finance REST API.
 *
 * All frontend usage must be gated behind the `finance-ledger-enabled`
 * LaunchDarkly flag.
 */

export const getFinanceBalance = (
  organisationId: string,
  { silent = false }: { silent?: boolean } = {}
) => ({
  query: gql`
    query GetFinanceBalance($workspaceId: String!) {
      getFinanceBalance(workspaceId: $workspaceId) {
        poolRemaining
        totalRemaining
        creditsWithheld
        dollarsRemaining
        dollarsWithheld
        averageCPC
        currency
        teamBalances {
          teamId
          teamName
          creditsRemaining
          creditsWithheld
          dollarsRemaining
          dollarsWithheld
        }
      }
    }
  `,
  module: 'finance',
  buildVariables: () => ({ workspaceId: organisationId }),
  fetchPolicy: 'network-only',
  isSilent: silent
});

export const getFinanceBillables = (
  organisationId: string
) => ({
  query: gql`
    query GetFinanceBillables(
      $workspaceId: String!
      $productRecordId: String!
    ) {
      getFinanceBillables(
        workspaceId: $workspaceId
        productRecordId: $productRecordId
      ) {
        productRecordId
        platformUrl
        records {
          recordId
          isPrimary
          type
          desc
          displayDescription
          chargedAs
          amount
          status
          teamAllocations
          createdAt
        }
      }
    }
  `,
  module: 'finance',
  buildVariables: (data: { productRecordId: string }) => ({
    workspaceId: organisationId,
    productRecordId: data.productRecordId
  }),
  ignoreCache: true
});

export const getFinanceCreditTransactions = (
  organisationId: string,
  { silent = false }: { silent?: boolean } = {}
) => ({
  query: gql`
    query GetFinanceCreditTransactions(
      $workspaceId: String!
      $from: String
      $to: String
      $limit: Int
      $offset: Int
      $search: String
    ) {
      getFinanceCreditTransactions(
        workspaceId: $workspaceId
        from: $from
        to: $to
        limit: $limit
        offset: $offset
        search: $search
      ) {
        imported
        total
        transactions {
          recordId
          createdAt
          type
          productOrigin
          productRecordId
          platformUrl
          chargedAs
          amount
          status
          requestedBy
          authorDisplay
          desc
          isPrimary
          legacyAddonsTotal
          addonCreditsTotal
          hasOverwrittenHistory
          originalSubmittedAt
          paidVia
          effectiveStatus
          settlementChargedAs
          parentReadableId
        }
      }
    }
  `,
  module: 'finance',
  buildVariables: (
    data: {
      from?: string;
      to?: string;
      limit?: number;
      offset?: number;
      search?: string;
    } = {}
  ) => ({
    workspaceId: organisationId,
    from: data.from,
    to: data.to,
    limit: data.limit,
    offset: data.offset,
    search: data.search
  }),
  fetchPolicy: 'network-only',
  isSilent: silent
});

export const allocateTeamCredits = (organisationId: string) => ({
  mutation: gql`
    mutation AllocateTeamCredits(
      $workspaceId: String!
      $teamId: String!
      $teamName: String
      $amount: Int!
    ) {
      allocateTeamCredits(
        workspaceId: $workspaceId
        teamId: $teamId
        teamName: $teamName
        amount: $amount
      ) {
        message
        recordIds
      }
    }
  `,
  module: 'finance',
  buildVariables: (data: { teamId: string; teamName?: string; amount: number }) => ({
    workspaceId: organisationId,
    teamId: data.teamId,
    teamName: data.teamName,
    amount: data.amount
  })
});

export const billFinanceWorkspace = (organisationId: string) => ({
  mutation: gql`
    mutation BillFinanceWorkspace(
      $workspaceId: String!
      $requestedBy: String!
      $productOrigin: String!
      $productRecordId: String!
      $platformUrl: String
      $billables: [FinanceBillableInput!]!
    ) {
      billFinanceWorkspace(
        workspaceId: $workspaceId
        requestedBy: $requestedBy
        productOrigin: $productOrigin
        productRecordId: $productRecordId
        platformUrl: $platformUrl
        billables: $billables
      ) {
        message
        overwritten
        newRecords
      }
    }
  `,
  module: 'finance',
  // Finance's 400s are actionable and caller-mapped (insufficient balance, a
  // missing team attribution, a stale-edit conflict). RequestProvider's default
  // `useGenericError` would replace the message with "Try refreshing the page."
  // before any caller sees it, which is what made these failures undiagnosable
  // from the UI. Both call sites — CostsTab's Shootsta Dollars save and
  // useVideoCredits' submitFinanceBillables — raise their own toast from the
  // returned message, so `isSilent` suppresses RequestProvider's duplicate one
  // rather than losing feedback.
  useGenericError: false,
  isSilent: true,
  buildVariables: (data: {
    requestedBy: string;
    productOrigin: string;
    productRecordId: string;
    platformUrl?: string;
    billables: Array<{
      isPrimary: boolean;
      type: string;
      desc: string;
      chargedAs: string;
      amount: number;
      status: string;
      teamAllocations?: any;
    }>;
  }) => ({
    workspaceId: organisationId,
    ...data,
    // Defense in depth: FinanceBillableInput.amount is a GraphQL Float. A string
    // amount (e.g. the platform outputCost "300" or an unparsed edit-field
    // value) makes Apollo reject the ENTIRE mutation
    // ("Float cannot represent non numeric value: \"300\""), so a valid sibling
    // credit row dies with it. Coerce every amount to a number at this single
    // request boundary so no caller / snapshot builder can reintroduce the bug.
    // Number('') / NaN collapse to 0 rather than propagating an invalid value.
    billables: (data.billables || []).map((billable) => ({
      ...billable,
      amount: Number(billable.amount) || 0
    }))
  })
});
